import React, { useMemo } from 'react';
import { ProductApi } from 'common/types';
import ContainerBox from '../ContainerBox';
import { useSelector } from 'react-redux';
import { useTranslation } from 'services/localization';
import * as LanguageSelectors from 'selectors/LanguageSelectors';
import * as ShopSelectors from 'selectors/ShopSelectors';
import * as StockSelectors from 'selectors/StockSelectors';
import * as UserSelectors from 'selectors/UserSelectors';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import ProductsTableToolbar from './ProductsTableToolbar';
import RentleDataGrid from 'components/datagrid/RentleDataGrid';
import { push, Routes } from 'routing';
import { getProductsTableColumns } from './columns';
import { productToProductsTableRow } from './rows';

interface Props {
	products: ProductApi[];
	searchValue?: string;
	loading?: boolean;
}
const ProductsTable = (props: Props) => {
	const { t } = useTranslation();
	const { products, searchValue, loading } = props;
	const lang = useSelector(LanguageSelectors.language);
	const pricingTables = useSelector(ShopSelectors.activeShopPricingTables);
	const currency = useSelector(ShopSelectors.getShopCurrency);
	const dateFormat = useSelector(ShopSelectors.getShopDateFormat);
	const categoriesById = useSelector(StockSelectors.categoriesById);
	const openingHours = useSelector(ShopSelectors.activeLocationOpeningHoursWithException);
	const userAccess = useSelector(UserSelectors.userActiveShopAccess);
	const shopDepositPaymentMethods = useSelector(ShopSelectors.shopDepositPaymentMethods);
	const shopLocationCount = useSelector(ShopSelectors.allShopLocationsCount);
	const showDeposit = !!shopDepositPaymentMethods.length;

	const apiRef = useGridApiRef();

	const columns = useMemo(() => {
		return getProductsTableColumns({
			t,
			currency,
			dateFormat,
			showDeposit,
		});
	}, [t, currency, dateFormat, showDeposit]);

	const rows = useMemo(() => {
		const mapFunction = productToProductsTableRow({
			t,
			lang,
			pricingTables,
			openingHours,
			categoriesById,
			userAccess,
			shopLocationCount,
		});
		return products.map(mapFunction);
	}, [
		products,
		t,
		pricingTables,
		openingHours,
		categoriesById,
		lang,
		userAccess,
		shopLocationCount,
	]);

	return (
		<ContainerBox padding="none">
			<RentleDataGrid
				apiRef={apiRef}
				autoHeight
				rows={rows}
				columns={columns}
				density="comfortable"
				rowHeight={80}
				components={{
					Toolbar: ProductsTableToolbar,
				}}
				pagination
				onRowClick={(params) => push(Routes.ProductPage(params.id as string))}
				loading={loading}
				defaultSortModel={[
					{
						field: 'name',
						sort: 'asc',
					},
				]}
				componentsProps={{
					noRowsOverlay: {
						title: !!searchValue
							? t('ProductsTable.noSearchResults', {
									searchValue,
									defaultValue: 'No matching products found for "{{searchValue}}"',
							  })
							: t('ProductsTable.noProducts', 'You have no products'),
						body: !!searchValue
							? ''
							: t(
									'ProductsTable.noProductsDescriptionNew',
									"Create products to make your offering available to your customers. Once you've created a product, you can manage it here!",
							  ),
					},
				}}
			/>
		</ContainerBox>
	);
};

export default ProductsTable;
